// Automatisch gegenereerd door tools/generate-dto.mjs — niet handmatig wijzigen.
// Bij herGeneratie worden imports, constructor-body en static create bewaard.

import { IProjectData, projectData } from 'src/app/features/project/models/project-data';
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';
import { toDateOnlyString } from 'src/app/infrastructure/functions/to-date-only-string.function';
import { ILevelProjectDto } from 'src/app/infrastructure/models/level-project-dto';
import { IContactDto } from './contact-dto.g';
import { IDomainDto } from './domain-dto.g';
import { IProjectCategoryDto } from './project-category-dto.g';
import { IImageBoxDto } from './image-box-dto.g';

export const projectDto = {
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    description: 'description',
    customer_Description: 'customer_Description',
    projectCategory_Guid: 'projectCategory_Guid',
    customer_Guid: 'customer_Guid',
    customerDescription: 'customerDescription',
    address: 'address',
    streetNr: 'streetNr',
    postalCode: 'postalCode',
    town: 'town',
    county: 'county',
    lastVisit: 'lastVisit',
    dateStart: 'dateStart',
    dateClose: 'dateClose',
    dateCloseDay: 'dateCloseDay',
    countryCode: 'countryCode',
    timeZoneId: 'timeZoneId',
    dateCreditEnd: 'dateCreditEnd',
    inactive: 'inactive',
    deleted: 'deleted',
    customerDto: 'customerDto',
    domainDto: 'domainDto',
    projectCategoryDto: 'projectCategoryDto',
    photoBoxDto: 'photoBoxDto',
} as const;

export type TProjectDto = (typeof projectDto)[keyof typeof projectDto];

export const projectDtoConstraints = {
    name: { maxLength: 40, minLength: 1 },
} as const;

export type TProjectDtoConstraints = typeof projectDtoConstraints;

export interface IProjectDto extends ILevelProjectDto {
    [projectDto.guid]: string | null;
    [projectDto.nr]: number | null;
    [projectDto.name]: string | null;
    [projectDto.description]: string | null;
    [projectDto.customer_Description]: string | null;
    [projectDto.projectCategory_Guid]: string | null;
    [projectDto.customer_Guid]: string | null;
    [projectDto.customerDescription]: string | null;
    [projectDto.address]: string | null;
    [projectDto.streetNr]: string | null;
    [projectDto.postalCode]: string | null;
    [projectDto.town]: string | null;
    [projectDto.county]: string | null;
    [projectDto.lastVisit]: string | null;
    [projectDto.dateStart]: string | null;
    [projectDto.dateClose]: string | null;
    [projectDto.dateCloseDay]: string | null;
    [projectDto.countryCode]: string | null;
    [projectDto.timeZoneId]: string | null;
    [projectDto.dateCreditEnd]: string | null;
    [projectDto.inactive]: boolean | null;
    [projectDto.deleted]: boolean | null;
    [projectDto.customerDto]: IContactDto | null;
    [projectDto.domainDto]: IDomainDto | null;
    [projectDto.projectCategoryDto]: IProjectCategoryDto | null;
    [projectDto.photoBoxDto]: IImageBoxDto | null;
}

export class ProjectDto
    implements IProjectDto
{
    [projectDto.guid]: string | null = null;
    [projectDto.nr]: number | null = null;
    [projectDto.name]: string | null = null;
    [projectDto.description]: string | null = null;
    [projectDto.customer_Description]: string | null = null;
    [projectDto.projectCategory_Guid]: string | null = null;
    [projectDto.customer_Guid]: string | null = null;
    [projectDto.customerDescription]: string | null = null;
    [projectDto.address]: string | null = null;
    [projectDto.streetNr]: string | null = null;
    [projectDto.postalCode]: string | null = null;
    [projectDto.town]: string | null = null;
    [projectDto.county]: string | null = null;
    [projectDto.lastVisit]: string | null = null;
    [projectDto.dateStart]: string | null = null;
    [projectDto.dateClose]: string | null = null;
    [projectDto.dateCloseDay]: string | null = null;
    [projectDto.countryCode]: string | null = null;
    [projectDto.timeZoneId]: string | null = null;
    [projectDto.dateCreditEnd]: string | null = null;
    [projectDto.inactive]: boolean | null = null;
    [projectDto.deleted]: boolean | null = null;
    [projectDto.customerDto]: IContactDto | null = null;
    [projectDto.domainDto]: IDomainDto | null = null;
    [projectDto.projectCategoryDto]: IProjectCategoryDto | null = null;
    [projectDto.photoBoxDto]: IImageBoxDto | null = null;

    private constructor(modelData?: IProjectData) {
        if (!modelData || isEmptyObject(modelData)) return;

        this[projectDto.nr] = modelData[projectData.nr];
        this[projectDto.guid] = modelData[projectData.guid];
        this[projectDto.name] = modelData[projectData.name];
        this[projectDto.description] = modelData[projectData.description];
        this[projectDto.projectCategory_Guid] = modelData[projectData.projectCategory_guid];
        this[projectDto.customer_Guid] = modelData[projectData.customer_guid];
        this[projectDto.customerDescription] = modelData[projectData.customerDescription];
        this[projectDto.address] = modelData[projectData.address];
        this[projectDto.streetNr] = modelData[projectData.streetNr];
        this[projectDto.postalCode] = modelData[projectData.postalCode];
        this[projectDto.town] = modelData[projectData.town];
        this[projectDto.county] = modelData[projectData.county];
        // Datums als datum-only string: een Date wordt bij JSON-serialisatie UTC, en lokaal middernacht
        // valt dan op de vorige dag. DateCloseDay is wat de backend leest (ProjectEditDto); elke save
        // (ook de description-tab) stuurt hem mee, anders ziet de server een leeg veld als heropenen.
        this[projectDto.dateStart] = toDateOnlyString(modelData[projectData.dateStart]);
        this[projectDto.dateCreditEnd] = toDateOnlyString(modelData[projectData.dateCreditEnd]);
        this[projectDto.dateClose] = toDateOnlyString(modelData[projectData.dateClose]);
        this[projectDto.dateCloseDay] = toDateOnlyString(modelData[projectData.dateClose]);
        const __countryId = modelData[projectData.country]?.itemId;
        this[projectDto.countryCode] = __countryId != null ? String(__countryId) : modelData[projectData.countryCode];
        this[projectDto.timeZoneId] = modelData[projectData.timeZoneId];
        this[projectDto.deleted] = modelData[projectData.deleted];
        this[projectDto.inactive] = modelData[projectData.inactive];
    }

    static create(modelData?: IProjectData | null): IProjectDto | null {
        return modelData ? new ProjectDto(modelData) : null;
    }
}
